import type { PortfolioAccount } from "../../Store/Reducers/CustomerWorkspaceSlice";

/** Account cadence / My cadence / Status overview filter options. */
export type CadenceFilterValue =
  | "all"
  | "current"
  | "stale"
  | "not_assessed"
  | "pending";

export const hasPendingFormal = (account: PortfolioAccount): boolean =>
  account.status_phase === "pending" || (account.pending_formal_count ?? 0) > 0;

export const hasOrgRequestPending = (account: PortfolioAccount): boolean =>
  account.org_status_phase === "pending";

/** Classifies My cadence from assessment_status. Draft folds into unassessed. */
export const classifyYourStatus = (
  status?: string | null
): "current" | "stale" | "not_assessed" => {
  const normalized = (status ?? "").trim().toLowerCase();
  if (normalized.startsWith("current")) return "current";
  if (normalized.startsWith("stale")) return "stale";
  return "not_assessed";
};

/**
 * Classifies Account cadence from org_assessment_status
 * (Unassessed · … / Current · … / Stale · …).
 */
export const classifyAccountStatus = (
  status?: string | null
): "current" | "stale" | "not_assessed" => {
  const normalized = (status ?? "").trim().toLowerCase();
  if (normalized.startsWith("current")) return "current";
  if (normalized.startsWith("stale")) return "stale";
  return "not_assessed";
};

export const formatCadenceLabel = (status?: string | null): string => {
  const raw = (status ?? "").trim();
  const normalized = raw.toLowerCase();
  if (
    !normalized ||
    normalized === "never assessed" ||
    normalized === "not assessed" ||
    normalized === "not published"
  ) {
    return "Unassessed";
  }
  return raw
    .replace(/\b(\d+)\s+days ago\b/i, "$1d")
    .replace(/\b1 day ago\b/i, "1d");
};

export type NeedsAttentionKind =
  | "pending"
  | "draft"
  | "unassessed"
  | "stale"
  | "current";

export type NeedsAttention = {
  kind: NeedsAttentionKind;
  label: string;
};

export const resolveNeedsAttention = (
  account: PortfolioAccount
): NeedsAttention => {
  const pending = account.pending_formal_count ?? 0;
  if (pending > 0) {
    return {
      kind: "pending",
      label: pending === 1 ? "1 pending" : `${pending} pending`,
    };
  }

  const yours = classifyYourStatus(account.assessment_status);
  const accountKind = classifyAccountStatus(account.org_assessment_status);
  if (accountKind === "not_assessed") {
    return { kind: "unassessed", label: "Unassessed" };
  }
  if (accountKind === "stale") {
    return {
      kind: "stale",
      label: formatCadenceLabel(account.org_assessment_status),
    };
  }
  if (yours === "stale") {
    return {
      kind: "stale",
      label: formatCadenceLabel(account.assessment_status),
    };
  }
  return {
    kind: "current",
    label: formatCadenceLabel(account.org_assessment_status) || "Current",
  };
};

export const needsAttentionClasses = (kind: NeedsAttentionKind): string => {
  if (kind === "pending") {
    return "bg-amber-50 text-amber-800 border border-amber-200";
  }
  if (kind === "draft") {
    return "bg-sky-50 text-sky-800 border border-sky-200";
  }
  if (kind === "unassessed") {
    return "bg-violet-50 text-violet-700 border border-violet-200";
  }
  if (kind === "stale") {
    return "bg-amber-50 text-amber-800 border border-amber-200";
  }
  return "bg-emerald-50 text-emerald-700 border border-emerald-200";
};

/** Overview KPI cards: Account cadence or Pending Formal/Team cycle. */
export const matchesAccountCadenceFilter = (
  account: PortfolioAccount,
  filter: CadenceFilterValue
): boolean => {
  if (filter === "all") return true;
  if (filter === "pending")
    return hasOrgRequestPending(account) || hasPendingFormal(account);
  return classifyAccountStatus(account.org_assessment_status) === filter;
};

/** Dropdown: filter by My cadence (assessment_status). */
export const matchesMyCadenceFilter = (
  account: PortfolioAccount,
  filter: CadenceFilterValue
): boolean => {
  if (filter === "all") return true;
  if (filter === "pending") return hasPendingFormal(account);
  return classifyYourStatus(account.assessment_status) === filter;
};

/**
 * Weekly Digest Assessment Status deep link: My cadence Stale OR Pending
 * (union — not the intersection of both).
 */
export const matchesDigestStaleOrPending = (
  account: PortfolioAccount
): boolean =>
  matchesMyCadenceFilter(account, "stale") && hasPendingFormal(account);

const CADENCE_FILTER_VALUES: readonly CadenceFilterValue[] = [
  "all",
  "current",
  "stale",
  "not_assessed",
  "pending",
] as const;

/** My cadence dropdown values (Pending is KPI-only). */
const MY_CADENCE_URL_VALUES: readonly CadenceFilterValue[] = [
  "all",
  "current",
  "stale",
  "not_assessed",
] as const;

export const parseMyCadenceFromUrl = (
  value: string | null
): CadenceFilterValue => {
  if (value && (MY_CADENCE_URL_VALUES as readonly string[]).includes(value)) {
    return value as CadenceFilterValue;
  }
  return "all";
};

export const parseKpiFilterFromUrl = (
  value: string | null
): CadenceFilterValue => {
  if (value && (CADENCE_FILTER_VALUES as readonly string[]).includes(value)) {
    return value as CadenceFilterValue;
  }
  return "all";
};

/** True when URL is the digest Status CTA (stale + pending together). */
export const isDigestStalePendingDeepLink = (
  myCadence: string | null,
  kpi: string | null
): boolean =>
  parseMyCadenceFromUrl(myCadence) === "stale" &&
  parseKpiFilterFromUrl(kpi) === "pending";
